import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import cn from '@/utils/cn';
import Icon from '@/components/ui/Icon';
import SPAButton from '../../shared/SPAButton';
import SPADialog from '../../shared/SPADialog';
import SPADropdown from '../../shared/SPADropdown';
import { useSPAToast } from '../../shared/SPAToast';
import {
  ProgramResource, ResourceOwner, resourceErrorMessage, useDeleteResource, useSaveResource,
} from './useProgramResources';

interface ResourceActionsProps {
  owner: ResourceOwner;
  resource: ProgramResource;
}

type OpenDialog = 'delete' | 'preview' | null;

const ErrorLine = ({ children }: { children: React.ReactNode }) => (
  <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-spa-error" role="alert">{children}</p>
);

// The row's actions menu, as the alert messages table has it: Edit opens the routed dialog,
// Preview shows the content as teachers see it, Publish and Delete act in place.
const ResourceActions = ({ owner, resource }: ResourceActionsProps) => {
  const {
    html, id, input_type: inputType, published, title, url,
  } = resource;
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const save = useSaveResource(owner);
  const destroy = useDeleteResource(owner);
  const spaToast = useSPAToast();

  const togglePublish = () => save.mutate({ id, resource: { published: !published } }, {
    onSuccess: saved => spaToast.success(`${saved.published ? 'Published' : 'Unpublished'} “${title}”.`),
  });

  // The row unmounts with the cache patch, taking the dialog with it.
  const confirmDelete = () => destroy.mutate(id, {
    onSuccess: () => spaToast.success(`Deleted “${title}”.`),
  });

  const handleDeleteOpenChange = (open: boolean) => {
    if (!open) destroy.reset();
    setOpenDialog(open ? 'delete' : null);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <SPADropdown>
        <SPADropdown.Trigger asChild>
          <SPAButton aria-label={`Actions for ${title}`} size="xs" variant="outline">
            Actions
            <Icon icon="ChevronDown" size={12} />
          </SPAButton>
        </SPADropdown.Trigger>
        <SPADropdown.Content className="tw-w-40">
          <SPADropdown.Item asChild>
            <Link to={`${id}/edit`}>Edit</Link>
          </SPADropdown.Item>
          <SPADropdown.Item onSelect={() => setOpenDialog('preview')}>Preview</SPADropdown.Item>
          <SPADropdown.Item disabled={save.isLoading} onSelect={togglePublish}>
            {published ? 'Unpublish' : 'Publish'}
          </SPADropdown.Item>
          <SPADropdown.Separator />
          <SPADropdown.Item className="tw-text-spa-error" onSelect={() => setOpenDialog('delete')}>
            Delete
          </SPADropdown.Item>
        </SPADropdown.Content>
      </SPADropdown>
      {save.isError && (
        <ErrorLine>{resourceErrorMessage(save.error, 'The resource could not be updated. Try again.')}</ErrorLine>
      )}

      <SPADialog open={openDialog === 'preview'} onOpenChange={open => setOpenDialog(open ? 'preview' : null)}>
        <SPADialog.Content size="lg">
          <SPADialog.Header>
            <SPADialog.Title>{title}</SPADialog.Title>
            <SPADialog.Description>{resource.name} · {inputType}</SPADialog.Description>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body>
            {inputType === 'Link' && (
              url
                ? <a href={url} rel="noreferrer" target="_blank">{url}</a>
                : <p className="tw-m-0 tw-text-sm tw-text-spa-tertiary">This resource has no URL yet.</p>
            )}
            {inputType !== 'Link' && (
              html ? (
                // Teachers see these on light pages, so the content sits on a white surface whatever
                // the SPA theme.
                <div
                  className={cn(
                    'fr-view tw-box-border tw-m-0 tw-w-full tw-overflow-x-auto tw-rounded tw-p-6',
                    'tw-border tw-border-solid tw-border-spa-border tw-bg-white tw-text-neutral-900',
                  )}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <p className="tw-m-0 tw-text-sm tw-text-spa-tertiary">This resource has no content yet.</p>
              )
            )}
          </SPADialog.Body>
        </SPADialog.Content>
      </SPADialog>

      <SPADialog open={openDialog === 'delete'} onOpenChange={handleDeleteOpenChange}>
        <SPADialog.Content size="sm">
          <SPADialog.Header>
            <SPADialog.Title>Delete resource</SPADialog.Title>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
            <SPADialog.Description>
              This permanently deletes “{title}”{published && ', which is currently published'}.
            </SPADialog.Description>
            {destroy.isError && (
              <ErrorLine>
                {resourceErrorMessage(destroy.error, 'The resource could not be deleted. Try again.')}
              </ErrorLine>
            )}
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Cancel</SPAButton>
            </SPADialog.Close>
            <SPAButton disabled={destroy.isLoading} variant="danger" onClick={confirmDelete}>
              Delete
            </SPAButton>
          </SPADialog.Footer>
        </SPADialog.Content>
      </SPADialog>
    </div>
  );
};

export default ResourceActions;
